import { useTranslation } from 'react-i18next'

export type FleetMode = 'fleet' | 'asset'

interface Props {
  fleetMode: FleetMode
  onFleetModeChange: (m: FleetMode) => void
  zonesEnabled: boolean
  onZonesEnabledChange: (v: boolean) => void
  // Time scrubber — units are minutes ago (window into the past).
  // 0 means "live", e.g. 30 means "show only devices seen in last 30 min".
  windowMin: number
  onWindowMinChange: (v: number) => void
}

/// Wave 1 E1.3 — Map V2 controls overlay.
///
/// Floats over the map and exposes the new Live Map 2.0 toggles:
///   - Fleet mode vs Asset mode (icon/colour styling — wired later)
///   - Custom zones layer on/off
///   - "Last seen ≤ N min" filter (the time scrubber primitive)
export function MapV2Toolbar({
  fleetMode,
  onFleetModeChange,
  zonesEnabled,
  onZonesEnabledChange,
  windowMin,
  onWindowMinChange,
}: Props) {
  const { t } = useTranslation()

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1000,
      }}
      className="bg-white rounded-full shadow-lg border border-slate-200 px-3 py-1.5 flex items-center gap-3 text-sm"
    >
      {/* Fleet/Asset toggle */}
      <div className="flex items-center bg-slate-100 rounded-full p-0.5">
        <button
          onClick={() => onFleetModeChange('fleet')}
          className={`px-3 py-1 rounded-full text-xs font-medium ${fleetMode === 'fleet' ? 'bg-sky-600 text-white' : 'text-slate-600'}`}
        >
          {t('map.mode.fleet', 'Fleet')}
        </button>
        <button
          onClick={() => onFleetModeChange('asset')}
          className={`px-3 py-1 rounded-full text-xs font-medium ${fleetMode === 'asset' ? 'bg-sky-600 text-white' : 'text-slate-600'}`}
        >
          {t('map.mode.asset', 'Asset')}
        </button>
      </div>

      {/* Zones */}
      <label className="inline-flex items-center gap-1 text-xs text-slate-700">
        <input
          type="checkbox"
          checked={zonesEnabled}
          onChange={(e) => onZonesEnabledChange(e.target.checked)}
          className="accent-sky-600"
        />
        {t('map.zones', 'Zones')}
      </label>

      {/* Time scrubber */}
      <div className="flex items-center gap-1 text-xs text-slate-700">
        <span>{t('map.lastSeen', 'Last seen')}</span>
        <input
          type="range"
          min={0}
          max={120}
          step={5}
          value={windowMin}
          onChange={(e) => onWindowMinChange(Number(e.target.value))}
          className="w-24 accent-sky-600"
        />
        <span className="tabular-nums w-10">
          {windowMin === 0 ? t('map.live', 'live') : `≤${windowMin}m`}
        </span>
      </div>
    </div>
  )
}
